import { computed, ref, unref } from 'vue'
import { storeToRefs } from 'pinia'
import axios from '@axios'
import { useRestaurantsStore } from '@/stores'

/**
 * The member's notes on one restaurant, as every row shows them
 * (Kapil, 2026-09-18).
 *
 * Three rules live here so the Favorites rows, the Discover cards and the chat
 * rows cannot drift apart:
 *   - a note is shown exactly as the member wrote it, restaurant name included
 *     (RM-346, 2026-09-29; this replaced the 9/18 rule that dropped the name)
 *   - notes stack, newest first, rather than hiding behind a count: a second
 *     note belongs under the first
 *   - past three the row would run away, so the rest wait behind "+N more"
 *
 * The notes come with the member (the `favoriteNotes` map carries up to five
 * per restaurant), so a row costs no request. Only a restaurant with more than
 * that fetches, and only when the member opens it.
 */
const NOTES_ON_ROW = 3

export function useRestaurantNotes(restaurantId, restaurantName) {
  const restaurantsStore = useRestaurantsStore()
  const { favoriteNotes } = storeToRefs(restaurantsStore)

  const id = computed(() => String(unref(restaurantId) || ''))
  const entry = computed(() => (id.value ? favoriteNotes.value[id.value] || null : null))

  const fetched = ref([])

  const allNotes = computed(() => {
    if (fetched.value.length) return fetched.value
    const carried = entry.value?.notes
    if (Array.isArray(carried) && carried.length) {
      return carried
        .map(n => ({ entryId: n.entryId, note: n.note || '' }))
        .filter(n => n.note)
    }
    const single = entry.value?.note || ''

    return single ? [{ entryId: entry.value?.entryId || null, note: single }] : []
  })

  const noteCount = computed(() => Number(entry.value?.count) || allNotes.value.length)
  const visibleNotes = computed(() => (expanded.value ? allNotes.value : allNotes.value.slice(0, NOTES_ON_ROW)))
  const hiddenCount = computed(() => Math.max(0, noteCount.value - visibleNotes.value.length))

  const expanded = ref(false)
  const loading = ref(false)

  async function toggle() {
    expanded.value = !expanded.value
    // Only a restaurant with more notes than the member payload carries needs a
    // request, and only the first time it is opened.
    if (!expanded.value || fetched.value.length || loading.value) return
    if (noteCount.value <= allNotes.value.length) return
    loading.value = true
    try {
      const { data } = await axios.get('/profile/entries', { params: { restaurantId: id.value } })

      fetched.value = (Array.isArray(data) ? data : [])
        .filter(e => e && e.kind !== 'contact')
        .map(e => ({ entryId: e.entryId || e._id, note: e.text || e.note || '' }))
        .filter(e => e.note)
    } catch (error) {
      // The count still reads true; opening it again retries.
    } finally {
      loading.value = false
    }
  }

  const moreLabel = computed(() => (
    expanded.value ? 'Fewer notes' : `+${hiddenCount.value} more note${hiddenCount.value === 1 ? '' : 's'}`
  ))

  const noteText = computed(() => allNotes.value[0]?.note || '')

  return { noteText, allNotes, visibleNotes, noteCount, hiddenCount, expanded, loading, toggle, moreLabel }
}
